import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, Navigate } from "react-router-dom";
import { Building2, Mail, MapPin, Phone, LogOut, Wrench, Trash2, Pencil } from "lucide-react";
import BypassEstablishmentIcon from "../components/BypassEstablishmentIcon";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import { toast } from "sonner";
import ChangePasswordCard from "../components/ChangePasswordCard";

export default function MerchantProfile() {
  const { role, commerce, logout, loading, refresh } = useAuth();
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  if (loading) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;
  if (role !== "commerce") return <Navigate to="/" replace />;

  const fullAddr = [commerce?.address, commerce?.postalCode, commerce?.city].filter(Boolean).join(", ");

  const startEditPhone = () => { setPhoneDraft(commerce?.phone || ""); setEditingPhone(true); };
  const savePhone = async (next) => {
    setSavingPhone(true);
    try {
      await api.patch("/merchant/me/phone", { phone: next });
      toast.success(next ? "Numéro mis à jour." : "Numéro supprimé.");
      setEditingPhone(false);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 max-w-2xl" data-testid="merchant-profile-page">
      <p className="overline mb-2">Mon établissement</p>
      <h1 className="font-display text-4xl font-black tracking-tighter text-ink">Profil établissement</h1>
      <p className="mt-2 text-subtle">Informations visibles dans votre espace BypassGo.</p>

      <div className="mt-8 border border-border divide-y divide-border" data-testid="merchant-profile-card">
        <Row icon={<Building2 size={18} />} label="Nom de l'établissement" value={commerce?.name} testid="merchant-profile-name" />
        <Row icon={<BypassEstablishmentIcon size={20} />} label="Type" value={commerce?.type} testid="merchant-profile-type" />
        <Row icon={<Mail size={18} />} label="Email" value={commerce?.email} testid="merchant-profile-email" />
        <Row icon={<MapPin size={18} />} label="Adresse" value={fullAddr} testid="merchant-profile-address" />
        <div className="flex items-start gap-4 p-4" data-testid="merchant-profile-phone">
          <div className="h-9 w-9 flex items-center justify-center bg-surface text-subtle shrink-0"><Phone size={18} /></div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-subtle font-bold">Téléphone <span className="text-subtle font-normal normal-case">(public)</span></p>
            {editingPhone ? (
              <div className="mt-1 flex gap-2 items-center">
                <Input
                  type="tel"
                  inputMode="numeric"
                  value={phoneDraft}
                  onChange={(e) => {
                    const digits = (e.target.value || "").replace(/\D+/g, "").slice(0, 10);
                    const formatted = digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
                    setPhoneDraft(formatted);
                  }}
                  placeholder="01 23 45 67 89"
                  maxLength={14}
                  className="h-10 flex-1"
                  data-testid="merchant-profile-phone-input"
                />
                <button
                  type="button"
                  onClick={() => savePhone(phoneDraft.trim())}
                  disabled={savingPhone}
                  className="h-10 px-3 rounded-md bg-brand hover:bg-brand-hover text-white font-bold text-xs uppercase tracking-wider disabled:opacity-50"
                  data-testid="merchant-profile-phone-save"
                >
                  {savingPhone ? "…" : "OK"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingPhone(false)}
                  disabled={savingPhone}
                  className="h-10 px-3 rounded-md border border-border text-subtle hover:border-ink text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                  data-testid="merchant-profile-phone-cancel"
                >
                  Annuler
                </button>
              </div>
            ) : (
              <div className="mt-0.5 flex items-center gap-2">
                <p className="text-ink font-medium break-words flex-1">
                  {commerce?.phone || <span className="text-subtle italic">Non renseigné</span>}
                </p>
                <button
                  type="button"
                  onClick={startEditPhone}
                  className="text-subtle hover:text-brand p-1.5"
                  aria-label={commerce?.phone ? "Modifier le téléphone" : "Ajouter un téléphone"}
                  data-testid="merchant-profile-phone-edit"
                >
                  <Pencil size={14} />
                </button>
                {commerce?.phone && (
                  <button
                    type="button"
                    onClick={() => savePhone("")}
                    disabled={savingPhone}
                    className="text-subtle hover:text-destructive p-1.5 disabled:opacity-50"
                    aria-label="Supprimer le téléphone"
                    data-testid="merchant-profile-phone-delete"
                    title="Supprimer le téléphone"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            )}
            <p className="mt-1 text-[11px] text-subtle leading-snug">
              Visible publiquement sur votre fiche de recherche. Vous pouvez le supprimer à tout moment.
            </p>
          </div>
        </div>
      </div>

      <p className="overline mt-10 mb-3">Raccourcis</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LinkCard to="/merchant/dashboard" icon={<BypassEstablishmentIcon size={18} className="brightness-0 invert" />} label="Tableau de bord" />
        <LinkCard to="/my-requests" icon={<Wrench size={16} />} label="Support" />
      </div>

      {/* Spec utilisateur Feb 2026 — changement de mot de passe disponible
         pour les comptes établissement directement depuis leur profil.
         Email de confirmation envoyé automatiquement après succès. */}
      <div className="mt-8">
        <ChangePasswordCard endpoint="/merchant/change-password" testIdPrefix="merchant-change-password" />
      </div>

      <div className="mt-10 pt-6 border-t border-border">
        <Button onClick={logout} variant="outline" className="border border-ink text-ink hover:bg-ink hover:text-white rounded-md h-12 px-6" data-testid="merchant-profile-logout-btn">
          <LogOut size={16} className="mr-2" /> Se déconnecter
        </Button>
      </div>
    </div>
  );
}

function Row({ icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-4 p-4" data-testid={testid}>
      <div className="h-9 w-9 flex items-center justify-center bg-surface text-subtle shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider text-subtle font-bold">{label}</p>
        <p className="mt-0.5 text-ink font-medium break-words">{value || <span className="text-subtle italic">Non renseigné</span>}</p>
      </div>
    </div>
  );
}

function LinkCard({ to, icon, label }) {
  return (
    <Link to={to} className="border border-border p-4 flex items-center gap-3 hover:border-ink transition-colors">
      <div className="h-8 w-8 flex items-center justify-center bg-brand text-white">{icon}</div>
      <span className="font-medium text-ink">{label}</span>
    </Link>
  );
}
